import {
	prefersReducedMotion,
	REDUCE_MOTION_QUERY,
} from '../utils/reducedMotion';

/** WCAG 2.2.2 threshold — pause control required when autoplay lasts longer than this. */
const PAUSE_CONTROL_SECONDS = 5;

function getVideos(root: HTMLElement): HTMLVideoElement[] {
	if (root.matches('video')) {
		return [root as HTMLVideoElement];
	}
	return Array.from(
		root.querySelectorAll<HTMLVideoElement>('video[data-autoplay-video]'),
	);
}

function createToggleButton(): HTMLButtonElement {
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'autoplay-video-toggle';
	button.setAttribute('data-autoplay-video-toggle', '');
	return button;
}

function setToggleState(button: HTMLButtonElement, playing: boolean) {
	button.setAttribute(
		'aria-label',
		playing ? 'Pause video' : 'Play video',
	);
	button.dataset.playing = playing ? 'true' : 'false';
	button.innerHTML = playing
		? `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><rect x="4" y="3" width="4" height="14" fill="currentColor"/><rect x="12" y="3" width="4" height="14" fill="currentColor"/></svg>`
		: `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M6 3.5v13l11-6.5L6 3.5Z" fill="currentColor"/></svg>`;
}

async function playVideos(videos: HTMLVideoElement[]) {
	const visible = videos.filter(
		(video) => getComputedStyle(video).display !== 'none',
	);
	const targets = visible.length ? visible : videos;
	await Promise.all(
		targets.map((video) => video.play().catch(() => undefined)),
	);
	videos.forEach((video) => {
		if (!targets.includes(video)) video.pause();
	});
}

function pauseVideos(videos: HTMLVideoElement[]) {
	videos.forEach((video) => {
		video.pause();
	});
}

function anyPlaying(videos: HTMLVideoElement[]): boolean {
	return videos.some((video) => !video.paused && !video.ended);
}

function knownDurations(videos: HTMLVideoElement[]): number[] {
	return videos
		.map((video) => video.duration)
		.filter((d): d is number => Number.isFinite(d) && d > 0);
}

function videoHasSource(video: HTMLVideoElement): boolean {
	return Boolean(
		video.currentSrc ||
			video.getAttribute('src') ||
			video.querySelector('source[src]'),
	);
}

function attachToggle(root: HTMLElement, videos: HTMLVideoElement[]) {
	if (root.querySelector('[data-autoplay-video-toggle]')) return;

	const button = createToggleButton();
	root.appendChild(button);

	const sync = () => setToggleState(button, anyPlaying(videos));

	button.addEventListener('click', () => {
		if (anyPlaying(videos)) {
			pauseVideos(videos);
		} else {
			void playVideos(videos).then(sync);
		}
		sync();
	});

	videos.forEach((video) => {
		video.addEventListener('play', sync);
		video.addEventListener('pause', sync);
	});

	sync();
}

function prepareVideos(videos: HTMLVideoElement[]) {
	videos.forEach((video) => {
		video.loop = true;
		video.muted = true;
		video.playsInline = true;
		video.removeAttribute('autoplay');
		video.setAttribute('aria-hidden', 'true');
	});
}

function initRoot(root: HTMLElement) {
	if (root.dataset.autoplayVideoInit === 'true') return;

	const videos = getVideos(root);
	if (!videos.length) return;

	root.dataset.autoplayVideoInit = 'true';
	prepareVideos(videos);

	if (prefersReducedMotion()) {
		pauseVideos(videos);
		return;
	}

	let settled = false;

	const onReady = () => {
		if (settled) return;

		// Wait until every sourced video has reported duration (lazy may still be empty).
		const sourced = videos.filter(videoHasSource);
		if (!sourced.length) return;
		if (sourced.some((video) => !knownDurations([video]).length)) return;

		settled = true;
		const durations = knownDurations(sourced);

		void playVideos(videos);

		if (Math.max(...durations) > PAUSE_CONTROL_SECONDS) {
			attachToggle(root, videos);
		}
	};

	videos.forEach((video) => {
		video.addEventListener('loadedmetadata', onReady);
		if (knownDurations([video]).length) onReady();
	});
}

/**
 * Ambient looping videos: no play under reduced motion; pause control only
 * when duration exceeds 5s (WCAG 2.2.2).
 */
class AutoplayVideos {
	constructor() {
		this.init();
		window
			.matchMedia(REDUCE_MOTION_QUERY)
			.addEventListener('change', () => this.init({ force: true }));
	}

	init({ force = false }: { force?: boolean } = {}) {
		const roots = document.querySelectorAll<HTMLElement>(
			'[data-autoplay-video-root]',
		);

		roots.forEach((root) => {
			if (force) {
				delete root.dataset.autoplayVideoInit;
				root
					.querySelectorAll('[data-autoplay-video-toggle]')
					.forEach((el) => el.remove());
			}
			initRoot(root);
		});
	}
}

export default AutoplayVideos;

/** Call after a lazy video source has been hydrated. */
export function maybePlayAutoplayVideo(video: HTMLVideoElement) {
	if (!video.hasAttribute('data-autoplay-video')) return;
	if (prefersReducedMotion()) {
		video.pause();
		return;
	}
	// Play once metadata is known so duration-gated controls can attach.
	const play = () => {
		void video.play().catch(() => undefined);
	};
	if (knownDurations([video]).length) {
		play();
	} else {
		video.addEventListener('loadedmetadata', play, { once: true });
	}
}
